import Button from 'components/ui/Button';
import Drawer from 'components/Drawer';
import Icons from 'components/icons';
import KnowledgeHub from 'compositions/KnowledgeHub';
import LeaveConfirmation from 'compositions/LeaveConfirmation';
import Position from 'components/ui/Position';
import Sidebar, { SidebarProps } from 'compositions/Sidebar';
import { AdjustmentFactory } from 'factories/adjustmentFactory';
import { AdjustmentUtil, LightingSolutionUtil, RoomUtil } from 'utils';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useSwipeDetect } from 'hooks';
import {
  selectAdjustments,
  selectLightingSolutionState,
  selectRoomState,
  setLightingSolution,
  setAdjustments as setCurrentAdjustments,
  selectControlType,
  setControlType,
  setSelectedRoomOptions,
  setRoom,
} from 'store/roomSlice';
import type { Adjustment, LightingSolution, NextArray, Option } from 'type';
import { useTranslation } from 'hooks/useTranslation';
import { useGoogleTagManager } from '@grace-studio/graceful-next/hooks';

import { selectUiState, setUi } from 'store/uiSlice';
import MiniMenu from 'compositions/MiniMenu';

const PANELS = {
  none: -1,
  lightingSolutions: 1,
  adjustments: 2,
};

const Sidebars = () => {
  const router = useRouter();
  const translation = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const gtm = useGoogleTagManager();
  useSwipeDetect({
    element: panelRef.current,
    configuration: { direction: 'left' },
    onSwipe: () => {
      setActivePanel(PANELS.none);
    },
  });

  const [activePanel, setActivePanel] = useState(PANELS.none);
  const [isQuitConfirmationOpen, setIsQuitConfirmationOpen] = useState(false);
  // const [isKnowledgeHubOpen, setIsKnowledgeHubOpen] = useState(false);

  const [adjustments, setAdjustments] = useState<NextArray<Adjustment>>([]);
  const [controlTypeOptions, setControlTypeOptions] = useState<
    NextArray<Option>
  >([]);
  const [lightingSolutions, setLightingSolutions] = useState<
    NextArray<LightingSolution>
  >([]);

  const dispatch = useAppDispatch();
  const currentRoom = useAppSelector(selectRoomState);
  const currentLightingSolution = useAppSelector(selectLightingSolutionState);
  const currentAdjustments = useAppSelector(selectAdjustments);
  const currentControlType = useAppSelector(selectControlType);
  const ui = useAppSelector(selectUiState) || {};

  const isSidebarOpen = () => activePanel !== PANELS.none;
  const isControlTypeSet = () =>
    currentControlType === 'presence' ||
    currentControlType === 'presenceDaylight';

  const handleLightingSolutionOnClick = (option: Option) => {
    gtm.trackEvent({ event: 'select_radio' });
    dispatch(setLightingSolution(option));
  };
  const handleAdjustmentOnClick = (option: Option) => {
    if (option.category === 'clo') {
      gtm.trackEvent({ event: 'select_toggle' });
    } else {
      gtm.trackEvent({ event: 'select_radio' });
    }
    dispatch(setCurrentAdjustments(option));
  };
  const handleControlTypeOnClick = (option: Option) => {
    gtm.trackEvent({ event: 'select_radio' });
    dispatch(setControlType(option));
  };

  const setPanel = (panel: number) => {
    if (activePanel === -1) {
      setActivePanel(panel);
    } else if (activePanel === panel) {
      setActivePanel(-1);
    } else {
      setActivePanel(panel);
    }
  };

  // const slugs = currentRoom?.lightingSolutions.map((s) => s.slug.current);
  // console.log('S L U G S ->', slugs);

  useEffect(() => {
    if (currentLightingSolution) {
      const _controlTypeOptions: NextArray<Option> =
        currentLightingSolution.cleValues.map(({ controlType, _key }) => ({
          _key,
          slug: { current: controlType },
          title: translation(`lightingSolutions.controlType.${controlType}`),
          selected: currentControlType === controlType,
        }));

      setControlTypeOptions(_controlTypeOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLightingSolution, currentControlType]);

  useEffect(() => {
    if (currentRoom) {
      const _adjustments = AdjustmentFactory.applySelected(
        currentRoom.adjustments,
        currentAdjustments,
      );
      setAdjustments(_adjustments);

      const _lightingSolutions = currentRoom.lightingSolutions.map(
        (lightingSolution) => ({
          ...lightingSolution,
          selected: RoomUtil.hasEqualSlug(
            lightingSolution,
            currentLightingSolution,
          ),
        }),
      );
      setLightingSolutions(_lightingSolutions);
    }
  }, [currentRoom, currentLightingSolution, currentAdjustments]);

  const lightingSolutionSidebar: SidebarProps = {
    title: translation('room.controls.lightingSolution'),
    optionsGroups: [
      {
        _key: 'recessed',
        title: translation('lightingSolutions.type.recessed'),
        onClick: handleLightingSolutionOnClick,
        options: LightingSolutionUtil.filterWithCategory(
          lightingSolutions,
          'recessed',
        ),
      },
      {
        _key: 'pendant',
        title: translation('lightingSolutions.type.pendant'),
        onClick: handleLightingSolutionOnClick,
        options: LightingSolutionUtil.filterWithCategory(
          lightingSolutions,
          'pendant',
        ),
      },
      {
        _key: 'traditional',
        title: translation('lightingSolutions.type.traditional'),
        onClick: handleLightingSolutionOnClick,
        options: LightingSolutionUtil.filterWithCategory(
          lightingSolutions,
          'traditional',
        ),
      },
      {
        _key: 'activityBased',
        title: translation('lightingSolutions.type.activityBased'),
        onClick: handleLightingSolutionOnClick,
        options: LightingSolutionUtil.filterWithCategory(
          lightingSolutions,
          'activityBased',
        ),
      },
    ].filter((group) => group.options?.length),
  };

  const adjustmentSidebar: SidebarProps = {
    title: translation('room.controls.adjustments'),
    optionsGroups: [
      {
        _key: 'control-type',
        title: translation('room.optionsCategory.lightingControl'),
        onClick: handleControlTypeOnClick,
        options: controlTypeOptions,
      },
      {
        _key: 'presence-level',
        title: translation('room.optionsCategory.presenceLevel'),
        disabled: !isControlTypeSet(),
        onClick: handleAdjustmentOnClick,
        options: AdjustmentUtil.filterWithCategory(
          adjustments,
          'presence-level',
        ),
      },
      {
        _key: 'clo',
        title: translation('room.optionsCategory.clo'),
        switchControl: true,
        onClick: handleAdjustmentOnClick,
        options: AdjustmentUtil.filterWithCategory(adjustments, 'clo'),
      },
    ].filter((group) => group.options && group.options.length),
  };

  function handleLightingKnowledgeClick() {
    const newStatus =
      ui.dashboard?.lightingKnowledge?.status == 'open' ? 'closed' : 'open';

    dispatch(
      setUi({
        ...ui,
        dashboard: {
          ...ui?.dashboard,
          lightingKnowledge: {
            ...ui.dashboard?.lightingKnowledge,
            status: newStatus,
            // activeId: 'lighting-solutions',
          },
        },
      }),
    );
  }

  useEffect(() => {
    dispatch(
      setUi({
        ...ui,
        leftPanelOpen: isSidebarOpen(),
      }),
    );
  }, [activePanel]);

  return (
    <>
      <Drawer
        disableBackdrop
        speed="slow"
        className="z-[20]"
        isOpen={isSidebarOpen()}
        onClose={() => setActivePanel(PANELS.none)}
      >
        <div className="absolute top-0 bottom-0 left-0 bg-white -translate-x-full w-80" />
        <div ref={panelRef} className="flex h-full">
          <div className="relative w-sidebar-small lg:w-sidebar bg-white">
            <Sidebar
              {...lightingSolutionSidebar}
              className={
                activePanel === PANELS.lightingSolutions ? '' : 'scale-0'
              }
            />
            <Sidebar
              {...adjustmentSidebar}
              className={activePanel === PANELS.adjustments ? '' : 'scale-0'}
            />

            <div
              className={`absolute top-1/2 -translate-y-1/2 transition duration-700 right-0 ${
                isSidebarOpen() ? 'translate-x-full' : ''
              }`}
            >
              <button
                aria-label="Close panel"
                onClick={() => setActivePanel(PANELS.none)}
                className="bg-white flex items-center justify-center transition h-32 w-8 rounded-r-lg text-[12px] -translate-x-[2px]"
              >
                <Icons.Chevron className="text-blue rotate-180" />
              </button>
            </div>
          </div>

          <div
            className={`relative transition-all ease-bounce duration-700 ${
              isSidebarOpen() ? '-translate-x-6' : ''
            }`}
          >
            <Position position="topLeft">
              <div className="flex flex-row gap-4">
                <Button
                  iconOnly
                  color="white"
                  hotKey="N"
                  onClick={() => {
                    setIsQuitConfirmationOpen(true);
                    setActivePanel(PANELS.none);
                  }}
                >
                  <Icons.Home className="!h-6" />
                </Button>
                <MiniMenu
                  trigger={
                    <Button iconOnly>
                      <Icons.Question className="!h-6" />
                    </Button>
                  }
                />
              </div>
            </Position>

            <Position position="bottomLeft" className="grid gap-4">
              <Button
                id="lighting-solutions"
                color="blue"
                selected={activePanel === PANELS.lightingSolutions}
                onClick={() => setPanel(PANELS.lightingSolutions)}
                hotKey="S"
                // className={
                //   isSidebarOpen() && activePanel !== PANELS.lightingSolutions
                //     ? 'bg-grey-700'
                //     : ''
                // }
              >
                {translation('room.controls.lightingSolution')}
              </Button>

              <Button
                id="lighting-controls"
                color="blue"
                selected={activePanel === PANELS.adjustments}
                onClick={() => setPanel(PANELS.adjustments)}
                hotKey="C"
                // className={
                //   isSidebarOpen() && activePanel !== PANELS.adjustments
                //     ? 'bg-grey-700'
                //     : ''
                // }
              >
                {translation('room.controls.adjustments')}
              </Button>

              <Button
                color="blue"
                hotKey="K"
                id="lighting-knowledge"
                onClick={() => {
                  // setIsKnowledgeHubOpen(!isKnowledgeHubOpen);
                  handleLightingKnowledgeClick();

                  gtm.trackEvent({
                    event: 'btn_open_knowledge',
                  });
                }}
              >
                {translation('room.controls.lightingKnowledge')}
              </Button>
            </Position>
          </div>
        </div>
      </Drawer>

      <KnowledgeHub
        // isOpen={isKnowledgeHubOpen}
        isOpen={ui.dashboard?.lightingKnowledge?.status == 'open'}
        // onClose={() => setIsKnowledgeHubOpen(false)}
        onClose={handleLightingKnowledgeClick}
      />

      <LeaveConfirmation
        isOpen={isQuitConfirmationOpen}
        onConfirm={() => {
          setIsQuitConfirmationOpen(false);
          dispatch(setSelectedRoomOptions(null));
          dispatch(setRoom(null));
          router.push('/room');
        }}
        onClose={() => setIsQuitConfirmationOpen(false)}
      />
    </>
  );
};

export default Sidebars;
